import React, { useCallback } from 'react'
import { GestureResponderEvent, SafeAreaView } from 'react-native'
import { useRecoilState, useResetRecoilState } from 'recoil'
import { ScrollView } from 'react-native-gesture-handler'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useFocusEffect, useRoute } from '@react-navigation/native'
import { SaveCreditCardByToken } from '@/domain/usecases'
import { cardMaskByFlag } from '@/domain/models'
import { NavigationRoute } from '@/main/config/router-navigation'
import { useCreditCard, useErrorHandler, useNotify, useTheme, useThemeClient } from '@/presentation/hooks'
import { Box, Button, HStack, NavigationPagesProps, PageHeader, VStack } from '@/presentation/components'
import {
  AddNewCreditCard,
  creditCardState,
  CreditCard,
  isEditableState,
  isLoadingState
} from '@/presentation/pages/Cartao/components'
import { styles } from '@/presentation/pages/Cartao/styles'

type CreditCardRegistrationProps = {
  saveCardByToken: SaveCreditCardByToken
  navigation: NavigationPagesProps['navigation']
}

export const CreditCardRegistration: React.FC<CreditCardRegistrationProps> = ({ saveCardByToken, navigation }) => {
  const theme = useTheme()
  const notify = useNotify()
  const { getAccentColor } = useThemeClient()
  const { getCreditCardType } = useCreditCard()
  const [creditCard] = useRecoilState(creditCardState)
  const [isLoading, setLoading] = useRecoilState(isLoadingState)
  const [isEditable, setEditable] = useRecoilState(isEditableState)
  const resetCreditCard = useResetRecoilState(creditCardState)
  const handleError = useErrorHandler()

  const params = useRoute().params as { token: string } | undefined

  useFocusEffect(
    useCallback(() => {
      resetCreditCard()
      setEditable(true)
      setLoading(false)
    }, [])
  )

  const isValidDataCard = useCallback((): boolean => {
    return Boolean(
      creditCard.nome &&
      creditCard.numero &&
      creditCard.expiracao &&
      creditCard.expiracao.length >= 4 &&
      creditCard.expiracao.includes('/') &&
      creditCard.ccv &&
      !creditCard.numero.includes('*')
    )
  }, [creditCard])

  const handleSave = (event: GestureResponderEvent): void => {
    event.preventDefault()

    if (!params?.token) {
      handleError(new Error('Token de cadastro não informado na URL'))
      return
    }

    if (!isValidDataCard()) {
      handleError(new Error('Todos os dados do cartão devem ser informados'))
      return
    }

    setLoading(true)

    saveCardByToken
      .save({
        token: params.token,
        card: {
          codCliente: 0,
          email: '',
          type: getCreditCardType(creditCard.numero),
          mask: cardMaskByFlag[creditCard.type],
          ccv: creditCard.ccv,
          expiracao: creditCard.expiracao,
          nome: creditCard.nome,
          numero: creditCard.numero
        }
      })
      .then((message) => {
        notify.show(message)
        resetCreditCard()
        navigation.navigate(NavigationRoute.Login)
      })
      .catch(handleError)
      .finally(() => setLoading(false))
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <Box style={styles.content}>
          <PageHeader
            text='Cadastro de cartão de crédito'
            icon={<MaterialCommunityIcons size={24} color={theme.colors.grey} name='credit-card-outline' />}
          />
          <VStack justify='center' items='center'>
            <AddNewCreditCard isEditable={isEditable} isLoading={isLoading} />
            <CreditCard creditCard={creditCard} />
          </VStack>
          <HStack items='center' justify='end' mt={18} pr={16} center spacing={8}>
            <Button
              title='SALVAR'
              testID='cadastrar-cartao-salvar'
              variant='outlined'
              color={getAccentColor()}
              onPress={handleSave}
              disabled={isLoading}
            />
          </HStack>
        </Box>
      </ScrollView>
    </SafeAreaView>
  )
}
